import React, {
  useState,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Container,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Backdrop,
} from "@mui/material";

import CheckCircle from "@mui/icons-material/CheckCircle";
import RadioButtonUnchecked from "@mui/icons-material/RadioButtonUnchecked";

import { axiosAPI, axiosupload } from "../../Components/AxiosComponents/Axios";
import TextInput from "../../Components/InputComponents/TextInput";
import SelectInput from "../../Components/InputComponents/SelectInput";
import CommonButton from "../../Components/InputComponents/CommonButton";

import DocumentSubmission from "../CandidateProcess/Common/DocumentSubmission";
import Interview from "../CandidateProcess/Common/Interview";
import Attestation from "../CandidateProcess/Common/Attestation";

import Travel from "../CandidateProcess/Common/Travel";
import {
  errorNotify,
  infoNotify,
  successNotify,
} from "../../Components/ToastComponent/ToastNotification";
import { Link } from "react-router-dom";

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

const DynamicField = ({
  field,
  onValueChange,
  candidateID = {},
  handleFileChange,
  fileUploads = {},
  isSaved,
  setIsSaved,
}) => {
  const {
    RPF_Id,
    RPF_Name,
    RPF_Type,
    RPF_IsRequired,
    RPF_MaxLength,
    RPF_ValidValues,
    RPF_Value,
  } = field;

  const isRequired = RPF_IsRequired == 1;
  const value = RPF_Value !== undefined ? RPF_Value : "";

  const parseValidValues = (rawValues) => {
    if (Array.isArray(rawValues)) return rawValues;
    if (typeof rawValues === "string") {
      try {
        const parsed = JSON.parse(rawValues);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  };

  const handleChange = (val) => {
    onValueChange(RPF_Id, val); // Centralized change handler
    setIsSaved(false);
  };

  const FileRenderer = (filePath, previewURL) => {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const isFromAPI = !!filePath?.attachment && !!filePath?.fileName;
    const isUploadedFile = filePath instanceof File;

    // File name
    const fileName = isFromAPI
      ? filePath.fileName
      : isUploadedFile
      ? filePath.name
      : null;

    // File extension
    const extension = fileName?.split(".").pop()?.toLowerCase() || "";
    const isImage = [
      "jpg",
      "jpeg",
      "png",
      "gif",
      "webp",
      "bmp",
      "svg",
    ].includes(extension);

    // File URL
    const fileUrl = isFromAPI
      ? `${baseUrl}/${filePath.attachment}`
      : previewURL || null;

    // Safety check
    if (!fileUrl || !fileName) return null;

    // Render image or file link
    return isImage ? (
      <img
        src={fileUrl}
        alt={fileName}
        style={{ maxWidth: "100%", maxHeight: 200 }}
      />
    ) : (
      <a href={fileUrl} target="_blank" rel="noopener noreferrer">
        {fileName}
      </a>
    );
  };

  const renderField = () => {
    switch (RPF_Type) {
      case "ShortText":
      case "LongText":
        return (
          <Grid item xs={12} sm={6} mt={2} sx={{ overflow: "visible" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              {RPF_Name}{" "}
              {isRequired ? <span style={{ color: "red" }}>*</span> : ""}
            </Typography>
            <TextInput
              key={RPF_Id}
              placeholder={RPF_Name}
              variant="outlined"
              fullWidth
              multiline={RPF_Type === "LongText"}
              rows={RPF_Type === "LongText" ? 4 : 1}
              required={isRequired}
              inputProps={{
                maxLength: RPF_MaxLength ? parseInt(RPF_MaxLength) : undefined,
              }}
              value={value || ""}
              maxLength={RPF_MaxLength ? RPF_MaxLength : ""}
              onChange={(e) => handleChange(e.target.value)}
            />
          </Grid>
        );

      case "Number":
        return (
          <Grid item xs={12} sm={6} mt={2} sx={{ overflow: "visible" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              {RPF_Name}{" "}
              {isRequired ? <span style={{ color: "red" }}>*</span> : ""}
            </Typography>
            <TextInput
              key={RPF_Id}
              type="text"
              allowOnlyNumbers={true}
              placeholder={RPF_Name}
              variant="outlined"
              fullWidth
              required={isRequired}
              value={value === null ? "" : value}
              maxLength={RPF_MaxLength ? RPF_MaxLength : ""}
              onChange={(e) =>
                handleChange(
                  e.target.value === "" ? null : Number(e.target.value)
                )
              }
            />
          </Grid>
        );

      case "Date":
        return (
          <Grid item xs={12} sm={6} mt={2} sx={{ overflow: "visible" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              {RPF_Name}{" "}
              {isRequired ? <span style={{ color: "red" }}>*</span> : ""}
            </Typography>
            <TextInput
              key={RPF_Id}
              type="date"
              placeholder={RPF_Name}
              variant="outlined"
              fullWidth
              required={isRequired}
              value={value || ""}
              onChange={(e) => handleChange(e.target.value)}
            />
          </Grid>
        );

      case "Time":
        return (
          <Grid item xs={12} sm={6} mt={2} sx={{ overflow: "visible" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              {RPF_Name}{" "}
              {isRequired ? <span style={{ color: "red" }}>*</span> : ""}
            </Typography>
            <TextInput
              key={RPF_Id}
              type="time"
              placeholder={RPF_Name}
              variant="outlined"
              fullWidth
              required={isRequired}
              value={value || ""}
              onChange={(e) => handleChange(e.target.value)}
            />
          </Grid>
        );

      case "Checkbox":
        const checkOptions = parseValidValues(RPF_ValidValues);
        const selectedValues = Array.isArray(value) ? value : [];

        return (
          <Grid item xs={12} sm={6} mt={2} sx={{ overflow: "visible" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              {RPF_Name}{" "}
              {isRequired ? <span style={{ color: "red" }}>*</span> : ""}
            </Typography>
            {checkOptions.map((option, idx) => (
              <FormControlLabel
                key={idx}
                control={
                  <Checkbox
                    checked={selectedValues.includes(option)}
                    onChange={(e) => {
                      const updatedValues = e.target.checked
                        ? [...selectedValues, option]
                        : selectedValues.filter((val) => val !== option);
                      handleChange(updatedValues);
                    }}
                  />
                }
                label={option}
              />
            ))}
          </Grid>
        );

      case "Radio":
        const radioOptions = parseValidValues(RPF_ValidValues);
        return (
          <Grid item xs={12} sm={6} mt={2} sx={{ overflow: "visible" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              {RPF_Name}{" "}
              {isRequired ? <span style={{ color: "red" }}>*</span> : ""}
            </Typography>
            <FormControl
              required={isRequired}
              component="fieldset"
              sx={{ mt: 1 }}
            >
              <RadioGroup
                row // enables horizontal layout
                value={value}
                onChange={(e) => handleChange(e.target.value)}
              >
                {radioOptions.map((opt, idx) => (
                  <FormControlLabel
                    key={idx}
                    value={opt}
                    control={<Radio />}
                    label={opt}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Grid>
        );

      case "Dropdown":
        const dropdownOptions = parseValidValues(RPF_ValidValues);
        return (
          <Grid item xs={12} sm={6} mt={2} sx={{ overflow: "visible" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              {RPF_Name}{" "}
              {isRequired ? <span style={{ color: "red" }}>*</span> : ""}
            </Typography>
            <SelectInput
              value={value}
              onChange={(e) => handleChange(e.target.value)}
              options={dropdownOptions.map((opt) => ({
                label: opt,
                value: opt,
              }))}
            />
          </Grid>
        );

      case "Attachment":
        const uploadedFile = fileUploads?.[RPF_Id]; // New: get locally selected file
        const previewURL = uploadedFile
          ? URL.createObjectURL(uploadedFile)
          : null;

        return (
          <Grid item xs={12} sm={6} mt={2} sx={{ overflow: "visible" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              {RPF_Name}
              {isRequired && <span style={{ color: "red" }}>*</span>}
            </Typography>

            {/* File/Image preview box */}
            {(uploadedFile || RPF_Value) && (
              <Box
                sx={{
                  border: "1px solid #ccc",
                  borderRadius: "8px",
                  padding: 2,

                  backgroundColor: "#f9f9f9",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  maxHeight: 200,
                  overflow: "hidden",
                }}
              >
                {uploadedFile?.name
                  ? FileRenderer(uploadedFile, previewURL)
                  : FileRenderer(RPF_Value)}
              </Box>
            )}

            {/* Upload button box below */}
            <Box
              sx={{
                border: "1px dashed #1976d2",
                borderRadius: "8px",
                padding: 2,
                marginTop: uploadedFile || RPF_Value ? 2 : "",
                backgroundColor: "#e3f2fd",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Button
                variant="contained"
                component="label"
                sx={{
                  textTransform: "capitalize",
                  fontSize: 14,
                  fontFamily: "Geist, sans-serif",
                }}
              >
                {RPF_Value || uploadedFile ? "Re-Upload" : "Upload"}
                <input
                  type="file"
                  hidden
                  onChange={(e) =>
                    handleFileChange(field.RPF_Id, e.target.files[0])
                  }
                />
              </Button>
            </Box>
          </Grid>
        );

      case "IsCompleted":
        const checkoptions = parseValidValues(["0", "1"]);
        const selectedvalues = Array.isArray(value) ? value : [];

        return (
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              {RPF_Name}{" "}
              {isRequired ? <span style={{ color: "red" }}>*</span> : ""}
            </Typography>
            {checkoptions.map((option, idx) => (
              <FormControlLabel
                key={idx}
                control={
                  <Checkbox
                    checked={selectedvalues.includes(option)}
                    onChange={(e) => {
                      const updatedValues = e.target.checked
                        ? [...selectedvalues, option]
                        : selectedvalues.filter((val) => val !== option);
                      handleChange(updatedValues);
                    }}
                  />
                }
                label={option}
              />
            ))}
          </Grid>
        );

      default:
        return (
          <Grid item xs={12} sm={6} mt={2}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              {RPF_Name}{" "}
              {isRequired ? <span style={{ color: "red" }}>*</span> : ""}
            </Typography>
            <TextInput
              key={RPF_Id}
              placeholder={`${RPF_Name} (Unsupported Type: ${RPF_Type})`}
              variant="outlined"
              fullWidth
              onChange={(e) => handleChange(e.target.value)}
              maxLength={RPF_MaxLength ? RPF_MaxLength : ""}
            />
          </Grid>
        );
    }
  };

  return <>{renderField()}</>;
};

const SubProcedureTabs = ({
  subProcedures,
  onFieldValueChange,
  candidateID,
  handleFileChange,
  fileUploads = {},
  hasUnsavedChanges,

  pendingTabIndex,
  setPendingTabIndex,

  pendingSubTabIndex,
  setPendingSubTabIndex,
  setSubValue,
  subValue,

  showUnsavedDialog,
  setShowUnsavedDialog,
  unsavedDialogLocation,
  setUnsavedDialogLocation,

  initialProcessFlowData,
  currentProcessFlowData,
  mainValue,
  isSaved,
  setIsSaved,

  rpsid,
}) => {
  const handleSubChange = (event, newValue) => {
    if (
      hasUnsavedChanges(
        initialProcessFlowData,
        currentProcessFlowData,
        fileUploads
      )
    ) {
      if (isSaved != true) {
        setPendingSubTabIndex(newValue);
        setShowUnsavedDialog(true);
        setUnsavedDialogLocation("sub");
      } else {
        setSubValue(newValue);
        setShowUnsavedDialog(false);
        setUnsavedDialogLocation("sub");
      }
    } else {
      setSubValue(newValue);
      setShowUnsavedDialog(false);
      setUnsavedDialogLocation("sub");
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ boxShadow: "none" }}>
        <Tabs
          value={subValue}
          onChange={handleSubChange}
          aria-label="sub procedure tabs"
          indicatorColor="error"
          textColor="error"
          variant="scrollable"
          scrollButtons="auto"
          sx={{ fontFamily: "Geist, sans-serif" }}
        >
          {subProcedures.map((sub, index) => {
            const isCompletedField = sub.fields?.find(
              (field) => field.RPF_Type === "IsCompleted"
            );

            const value = [isCompletedField?.RPF_Value].flat()[0] || "0"; // Default to "0" (In Progress)
            const status = value; // "0", "1", or "2"


            const getBackgroundColor = () => {
              if (status === "1") {
                // Completed: green
                return subValue === index ? "#2E7D32" : "#C8FACC";
              } else if (status === "2") {
                // Draft: grey
                return subValue === index ? "#6B7280" : "#D1D5DB"; // Tailwind gray-600 / gray-300
              } else {
                // In Progress: blue
                return subValue === index ? "#006CB8" : "#EBF2FF";
              }
            };

            const getTextColor = () => {
              return subValue === index ? "white" : "black";
            };

            return (
              <Tab
                key={sub.RSP_Id}
                label={sub.RSP_Name}
                id={`sub-tab-${index}`}
                aria-controls={`sub-tabpanel-${index}`}
                sx={{
                  fontFamily: "Geist, sans-serif",
                  backgroundColor: getBackgroundColor(),
                  color: getTextColor(),
                  borderBottom: "4px solid #EAB308",
                  fontWeight: subValue === index ? 600 : 500,
                  fontWeight: 500,
                  fontSize: `12px`,
                  letterSpacing: "1px",
                  minWidth: "100px",
                  minHeight: "30px",
                  margin: "2px",
                }}
              />
            );
          })}
        </Tabs>
      </Box>
      {subProcedures.map((sub, index) => (
        <CustomTabPanel key={sub.RSP_Id} value={subValue} index={index}>
          {sub && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                my: 2,
              }}
            >
              <Typography
                variant="h5"
                sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}
              >
                {sub.RSP_Name}
              </Typography>

              {sub.fields?.find(
                (field) => field.RPF_Type === "IsCompleted"
              ) && (
                <Grid
                  item
                  sx={{ padding: "5px" }}
                  xs={12}
                  sm={7}
                  md={4}
                  lg={4}
                  xl={3}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
                  >
                    Status <span style={{ color: "red" }}> * </span>
                  </Typography>
                  <SelectInput
                    value={(() => {
                      const field = sub.fields.find(
                        (field) => field.RPF_Type === "IsCompleted"
                      );
                      if (!field) return "";

                      const val = Array.isArray(field.RPF_Value)
                        ? field.RPF_Value[0]
                        : field.RPF_Value;

                      if (val === "1") return "Completed";
                      if (val === "0") return "In Progress";
                      return "Draft";
                    })()}
                    name="IsCompleted"
                    onChange={(e) => {
                      const selected = e.target.value;
                      const mappedValue =
                        selected === "Completed"
                          ? ["1"]
                          : selected === "In Progress"
                          ? ["0"]
                          : ["2"]; // "2" assumed for Draft

                      const field = sub.fields.find(
                        (field) => field.RPF_Type === "IsCompleted"
                      );

                      onFieldValueChange(field?.RPF_Id, mappedValue);
                      setIsSaved(false);
                    }}
                    options={[
                      { value: "In Progress", label: "In Progress" },
                      { value: "Completed", label: "Completed" },
                      { value: "Draft", label: "Draft" },
                    ]}
                    placeholder="Select Status"
                  />
                </Grid>
              )}
            </Box>
          )}

          {sub.fields && sub.fields.length > 0 ? (
            <Box component="form" noValidate autoComplete="off">
              {/* Pass each field and the global change handler */}
              <Grid container spacing={2}>
                {sub.fields
                  ?.filter((f) => f.RPF_Type !== "IsCompleted")
                  .map((field) => (
                    <DynamicField
                      key={field.RPF_Id}
                      field={field}
                      onValueChange={onFieldValueChange}
                      candidateID={candidateID}
                      handleFileChange={handleFileChange}
                      fileUploads={fileUploads}
                      isSaved={isSaved}
                      setIsSaved={setIsSaved}
                    />
                  ))}
              </Grid>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No fields for this sub-procedure.
            </Typography>
          )}
        </CustomTabPanel>
      ))}
    </Box>
  );
};

const ProcessFlowViewer = ({
  candidateIDOriginal,
  candidateID,
  JAID,

  countryID,
  professionID,

  rpmID,
  originalValue,
  currentProcessFlowDataParent,
  loading,
  error,
  mainAPICallback = {},
  setMainAPICallback = {},
}) => {
  const [mainValue, setMainValue] = useState(0);
  const [subValue, setSubValue] = useState(0);

  const [currentProcessFlowData, setCurrentProcessFlowData] = useState(null);
  const [initialProcessFlowData, setInitialProcessFlowData] = useState(null);

  const [pendingTabIndex, setPendingTabIndex] = useState(null);
  const [pendingSubTabIndex, setPendingSubTabIndex] = useState(null);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [unsavedDialogLocation, setUnsavedDialogLocation] = useState("main");

  const [fileUploads, setFileUploads] = useState({});

  const handleFileChange = (rpfId, file) => {
    const MAX_SIZE_MB = 30;
    const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2); // rounded to 2 decimal places

    if (file.size > MAX_SIZE_BYTES) {
      infoNotify(
        `File size should be less than ${MAX_SIZE_MB} MB. Uploaded file size is ${fileSizeMB} MB`
      );
      return;
    }

    setFileUploads((prev) => ({
      ...prev,
      [rpfId]: file,
    }));
  };

  useEffect(() => {
    setCurrentProcessFlowData(currentProcessFlowDataParent);
    setInitialProcessFlowData(currentProcessFlowDataParent);
  }, [currentProcessFlowDataParent]);

  const handleFieldValueChange = useCallback((fieldId, newValue) => {
    setCurrentProcessFlowData((prevData) => {
      const newData = JSON.parse(JSON.stringify(prevData));

      let fieldFound = false;
      for (const stage of newData.stages) {
        if (stage.fields) {
          for (const field of stage.fields) {
            if (field.RPF_Id === fieldId) {
              field.RPF_Value = newValue;
              fieldFound = true;
              break;
            }
          }
        }
        if (fieldFound) break;

        if (stage.subProcedures) {
          for (const sub of stage.subProcedures) {
            if (sub.fields) {
              for (const field of sub.fields) {
                if (field.RPF_Id === fieldId) {
                  field.RPF_Value = newValue;
                  fieldFound = true;
                  break;
                }
              }
            }
            if (fieldFound) break;
          }
        }
        if (fieldFound) break;
      }
      return newData;
    });
  }, []);

  const hasUnsavedChanges = (original, current, fileUploads = {}) => {
    const getFieldMap = (stages) => {
      const map = {};
      stages.forEach((stage) => {
        (stage.fields || []).forEach((f) => (map[f.RPF_Id] = f.RPF_Value));
        (stage.subProcedures || []).forEach((sub) => {
          (sub.fields || []).forEach((f) => (map[f.RPF_Id] = f.RPF_Value));
        });
      });
      return map;
    };

    const originalMap = getFieldMap(original?.stages || []);
    const currentMap = getFieldMap(current?.stages || []);

    // 1. Detect normal field value changes
    const fieldChanged = Object.keys(currentMap).some((key) => {
      return (
        JSON.stringify(originalMap[key]) != JSON.stringify(currentMap[key])
      );
    });

    // 2. Detect file uploads not yet saved
    const fileChanged = Object.entries(fileUploads).some(([rpfId, file]) => {
      const hasFile = !!file;
      const currentValue = currentMap[rpfId];
      const originalValue = originalMap[rpfId];

      // If file is newly selected but not present in current or original data
      const fileIsUnsaved =
        hasFile && (!currentValue || currentValue == originalValue);

      return fileIsUnsaved;
    });

    return fieldChanged || fileChanged;
  };

  const handleMainChange = (event, newValue) => {
    if (
      hasUnsavedChanges(
        initialProcessFlowData,
        currentProcessFlowData,
        fileUploads
      )
    ) {
      if (isSaved != true) {
        setPendingTabIndex(newValue);
        setShowUnsavedDialog(true);
        setUnsavedDialogLocation("main");
      } else {
        setPendingTabIndex(newValue);
        setMainValue(newValue);
        setSubValue(0);
        setShowUnsavedDialog(false);
      }
    } else {
      setPendingTabIndex(newValue);
      setMainValue(newValue);
      setSubValue(0);
    }
  };

  const [isBtnDisabled, setIsBtnDisabled] = useState(false);

  const getRelevantFileUploads = (stage) => {
    const relevant = {};
    const fieldIds = new Set();

    (stage?.fields || []).forEach((f) => fieldIds.add(f.RPF_Id));
    (stage?.subProcedures || []).forEach((sub) =>
      (sub?.fields || []).forEach((f) => fieldIds.add(f.RPF_Id))
    );

    for (const [rpfId, file] of Object.entries(fileUploads)) {
      if (fieldIds.has(Number(rpfId))) {
        relevant[rpfId] = file;
      }
    }

    return relevant;
  };

  const uploadFiles = async () => {
    for (const [rpfId, file] of Object.entries(fileUploads)) {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("rpfId", rpfId);
      formData.append("candidateId", candidateID);
      formData.append("CRAD_JobApplicationID", JAID);

      setIsBtnDisabled(true);
      try {
        const res = await axiosupload.post(
          `/RecruitmentProcedure/upload-attachment`,
          formData
        );

        // ✅ Update currentProcessFlowData to reflect uploaded file
        const uploadedFileName = res?.data?.data?.fileName;
        if (uploadedFileName) {
          setCurrentProcessFlowData((prevData) => {
            const newData = JSON.parse(JSON.stringify(prevData));

            for (const stage of newData.stages) {
              let fieldFound = false;

              const updateField = (fields) => {
                for (const field of fields || []) {
                  if (field.RPF_Id == rpfId) {
                    field.RPF_Value = uploadedFileName; // or file URL/path if that's what backend returns
                    fieldFound = true;
                    break;
                  }
                }
              };

              updateField(stage.fields);
              if (!fieldFound) {
                for (const sub of stage.subProcedures || []) {
                  updateField(sub.fields);
                  if (fieldFound) break;
                }
              }
              if (fieldFound) break;
            }

            return newData;
          });
        }
      } catch (error) {
        errorNotify("Something went wrong!");
      } finally {
        setIsBtnDisabled(false);
      }
    }
  };

  const [isSaved, setIsSaved] = useState(false);

  const savefn = useCallback(
    async (stage, mainValue, subValue) => {
      if (
        !hasUnsavedChanges(
          initialProcessFlowData,
          currentProcessFlowData,
          fileUploads
        )
      ) {
        infoNotify("No Changes were made to save.");
      } else {
        setIsBtnDisabled(true);

        try {
          const hasMissingRequiredFields = stage?.fields?.some((field) => {
            const isRequired = field?.RPF_IsRequired == 1;
            const isAttachment = field?.RPF_Type == "Attachment";
            const valueEmpty = !field?.RPF_Value || field?.RPF_Value === "";

            if (!isRequired) return false;

            if (isAttachment) {
              // If attachment is required and value is empty
              const hasUploadedFile = fileUploads?.[field.RPF_Id];
              return valueEmpty && !hasUploadedFile;
            } else {
              // For non-attachments
              return valueEmpty;
            }
          });

          const sub = stage?.subProcedures?.[subValue];

          const hasMissingRequiredFieldsSub = sub?.fields?.some((field) => {
            const isRequired = field?.RPF_IsRequired == 1;

            if (!isRequired) return false;
            const isAttachment = field?.RPF_Type === "Attachment";
            const valueEmpty = !field?.RPF_Value || field?.RPF_Value === "";

            if (isAttachment) {
              const hasUploadedFile = fileUploads?.[field.RPF_Id];
              return valueEmpty && !hasUploadedFile;
            }

            return valueEmpty; // this runs ONLY for required non-attachments
          });

          const hasSubProcedures =
            Array.isArray(stage?.subProcedures) &&
            stage.subProcedures.length > 0;

          if (!hasSubProcedures) {
            if (hasMissingRequiredFields) {
              setIsBtnDisabled(false);
              errorNotify("Required fields missing. Cannot save.");
              return;
            }
          } else if (hasSubProcedures) {
            if (hasMissingRequiredFieldsSub) {
              setIsBtnDisabled(false);
              errorNotify("Required fields missing. Cannot save.");
              return;
            }
          }

          setIsSaved(true);

          // Upload API Cal
          await uploadFiles();

          // Data Send API Call
          setIsBtnDisabled(false);
          if (currentProcessFlowData) {
            const res = await axiosAPI.post(`/RecruitmentProcedure/save`, {
              candidateId: candidateID,
              rpmId: rpmID,
              procedureJson: currentProcessFlowData,
              jobapplicationId: JAID,
            });

            if (res?.data?.success) {
              successNotify(res?.data?.message);

              const newInitial = JSON.parse(
                JSON.stringify(currentProcessFlowData)
              );
              setInitialProcessFlowData(newInitial);

              // Clear only entries from fileUploads that are already reflected in currentProcessFlowData
              setFileUploads((prev) => {
                const getFieldMap = (stages = []) => {
                  const map = {};
                  stages.forEach((stage) => {
                    (stage.fields || []).forEach(
                      (f) => (map[f.RPF_Id] = f.RPF_Value)
                    );
                    (stage.subProcedures || []).forEach((sub) => {
                      (sub.fields || []).forEach(
                        (f) => (map[f.RPF_Id] = f.RPF_Value)
                      );
                    });
                  });
                  return map;
                };

                const fieldMap = getFieldMap(newInitial.stages || []);
                const newFileUploads = { ...prev };

                for (const rpfId in newFileUploads) {
                  // If the file has been reflected in current data, remove from fileUploads
                  if (fieldMap[rpfId]) {
                    delete newFileUploads[rpfId];
                  }
                }

                return newFileUploads;
              });

              setMainAPICallback(!mainAPICallback);
            } else {
              errorNotify("Something went wrong!");
            }
          }
        } catch (err) {
          setIsBtnDisabled(false);
          errorNotify("Something went wrong.");
        } finally {
          setIsBtnDisabled(false);
          // window.scrollTo({
          //   top: document.body.scrollHeight,
          //   behavior: "smooth",
          // });
        }
      }
    },
    [currentProcessFlowData, candidateID, rpmID, fileUploads]
  );

  if (loading) {
    return (
      <Container
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "40vh",
        }}
      >
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading process flow...</Typography>
      </Container>
    );
  }
  if (error) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }
  if (!currentProcessFlowData) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert severity="info">
          No process flow data available to display.
        </Alert>
      </Box>
    );
  }

  return (
    <Paper sx={{ boxShadow: "none", padding: "20px 0px" }}>
      <Paper
        elevation={3}
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: "4px",
          boxShadow: "none",
          overflow: "auto",
          padding: "20px",
        }}
      >
        <Box sx={{ boxShadow: "none" }}>
          <Tabs
            value={mainValue}
            onChange={handleMainChange}
            aria-label="main process flow tabs"
            indicatorColor="error"
            textColor="error"
            variant="scrollable"
            scrollButtons="auto"
            sx={{ fontFamily: "Geist, sans-serif" }}
          >
            {["Document Submission", "Interview Status"].map((label, index) => (
              <Tab
                key={label}
                label={label}
                id={`main-tab-${index}`}
                aria-controls={`main-tabpanel-${index}`}
                sx={{
                  fontFamily: "Geist, sans-serif",
                  background: mainValue == index ? "#006CB8" : "#EBF2FF",
                  border: "1px solid lightgrey",
                  borderBottom: "4px solid #EAB308",
                  color: mainValue == index ? "white" : "black",
                  fontWeight: 500,
                  fontSize: `14px`,
                  letterSpacing: "1px",
                  minWidth: "150px",
                  minHeight: "60px",
                  margin: "2px",
                }}
              />
            ))}

            {currentProcessFlowData.stages.map((stage, index) => {
              const hasSubProcedures =
                Array.isArray(stage.subProcedures) &&
                stage.subProcedures.length > 0;

              let stageCompletionStatus = "0"; // Default: In Progress

              const subComponents = hasSubProcedures
                ? stage.subProcedures.map((sub) => {
                    const fields = sub?.fields || [];

                    const isCompletedField = fields.find(
                      (field) => field?.RPF_Type === "IsCompleted"
                    );

                    const statusValue = Array.isArray(
                      isCompletedField?.RPF_Value
                    )
                      ? isCompletedField.RPF_Value[0]
                      : isCompletedField?.RPF_Value || "0";

                    return {
                      ...sub,
                      isCompleted: statusValue, // "1", "0", or "2"
                    };
                  })
                : [];

              if (hasSubProcedures) {
                // All sub-procedures must be completed (i.e., "1") to mark stage as completed
                const statuses = subComponents.map((s) => s.isCompleted);
                if (statuses.every((s) => s === "1")) {
                  stageCompletionStatus = "1";
                } else if (statuses.every((s) => s === "2")) {
                  stageCompletionStatus = "2";
                } else {
                  stageCompletionStatus = "0";
                }
              } else {
                const field = stage?.fields?.find(
                  (field) => field?.RPF_Type === "IsCompleted"
                );
                const value = Array.isArray(field?.RPF_Value)
                  ? field.RPF_Value[0]
                  : field?.RPF_Value;
                stageCompletionStatus = value || "0";
              }

              const getBackgroundColor = () => {
                if (stageCompletionStatus === "1") {
                  // Completed: green
                  return mainValue === index + 2 ? "#2E7D32" : "#C8FACC";
                } else if (stageCompletionStatus === "2") {
                  // Draft: grey
                  return mainValue === index + 2 ? "#6B7280" : "#D1D5DB"; // Tailwind gray-600 / gray-300
                } else {
                  // In Progress: blue
                  return mainValue === index + 2 ? "#006CB8" : "#EBF2FF";
                }
              };

              const getTextColor = () => {
                return mainValue === index + 2 ? "white" : "black";
              };

              return (
                <Tab
                  key={stage.RPS_Id}
                  label={stage.RPS_Name}
                  id={`main-tab-${index + 2}`}
                  aria-controls={`main-tabpanel-${index + 2}`}
                  sx={{
                    fontFamily: "Geist, sans-serif",
                    backgroundColor: getBackgroundColor(),
                    color: getTextColor(),
                    borderBottom: "4px solid #EAB308",
                    fontWeight: 500,
                    fontSize: `14px`,
                    letterSpacing: "1px",
                    minWidth: "150px",
                    minHeight: "60px",
                    margin: "2px",
                  }}
                />
              );
            })}
          </Tabs>
        </Box>

        {/* Static Panels */}
        <CustomTabPanel value={mainValue} index={0}>
          <DocumentSubmission
            candidateID={candidateID}
            JAID={JAID}
            countryID={countryID}
            professionID={professionID}
          />
        </CustomTabPanel>
        <CustomTabPanel value={mainValue} index={1}>
          <Interview
            candidateID={candidateID}
            candidateIDOriginal={candidateIDOriginal}
          />
        </CustomTabPanel>

        {/* Dynamic Panels */}
        {currentProcessFlowData.stages.map((stage, index) => (
          <CustomTabPanel
            key={stage.RPS_Name}
            value={mainValue}
            index={index + 2}
          >
            {stage.subProcedures?.length === 0 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  my: 2,
                }}
              >
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}
                >
                  {stage.RPS_Name}
                </Typography>

                {stage.fields?.find(
                  (field) => field.RPF_Type === "IsCompleted"
                ) && (
                  <Grid
                    item
                    sx={{ padding: "5px" }}
                    xs={12}
                    sm={7}
                    md={4}
                    lg={4}
                    xl={3}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
                    >
                      Status <span style={{ color: "red" }}> * </span>
                    </Typography>
                    <SelectInput
                      value={(() => {
                        const field = stage.fields.find(
                          (field) => field.RPF_Type === "IsCompleted"
                        );
                        if (!field) return "";

                        const val = Array.isArray(field.RPF_Value)
                          ? field.RPF_Value[0]
                          : field.RPF_Value;

                        if (val == "1") return "Completed";
                        if (val == "2") return "In Progress";
                        if (val == "0") return "Draft";
                        return "In Progress";
                      })()}
                      name="IsCompleted"
                      onChange={(e) => {
                        const selected = e.target.value;
                        const mappedValue =
                          selected === "Completed"
                            ? ["1"]
                            : selected === "In Progress"
                            ? ["0"]
                            : ["2"]; // Assuming "2" for Draft

                        const field = stage.fields.find(
                          (field) => field.RPF_Type === "IsCompleted"
                        );

                        handleFieldValueChange(field.RPF_Id, mappedValue);
                        setIsSaved(false);
                      }}
                      options={[
                        { value: "In Progress", label: "In Progress" },
                        { value: "Completed", label: "Completed" },
                        { value: "Draft", label: "Draft" },
                      ]}
                      placeholder="Select Status"
                      // optional: handle errors or refs if needed
                    />
                  </Grid>
                )}
              </Box>
            )}

            {stage.subProcedures && stage.subProcedures.length > 0 ? (
              <SubProcedureTabs
                subProcedures={stage.subProcedures}
                onFieldValueChange={handleFieldValueChange}
                candidateID={candidateID}
                handleFileChange={handleFileChange}
                fileUploads={fileUploads}
                hasUnsavedChanges={hasUnsavedChanges}
                pendingTabIndex={pendingTabIndex}
                setPendingTabIndex={setPendingTabIndex}
                subValue={subValue}
                setSubValue={setSubValue}
                pendingSubTabIndex={pendingSubTabIndex}
                setPendingSubTabIndex={setPendingSubTabIndex}
                showUnsavedDialog={showUnsavedDialog}
                setShowUnsavedDialog={setShowUnsavedDialog}
                unsavedDialogLocation={unsavedDialogLocation}
                setUnsavedDialogLocation={setUnsavedDialogLocation}
                initialProcessFlowData={initialProcessFlowData}
                currentProcessFlowData={currentProcessFlowData}
                mainValue={mainValue}
                isSaved={isSaved}
                setIsSaved={setIsSaved}
                rpsid={stage.RPS_Id}
              />
            ) : stage.fields && stage.fields.length > 0 ? (
              <Box component="form" noValidate autoComplete="off">
                <Grid container spacing={2}>
                  {stage.fields
                    ?.filter((f) => f.RPF_Type !== "IsCompleted")
                    .map((field) => (
                      <DynamicField
                        key={field.RPF_Id}
                        field={field}
                        onValueChange={handleFieldValueChange}
                        candidateID={candidateID}
                        handleFileChange={handleFileChange}
                        fileUploads={fileUploads} // <-- NEW PROP
                        isSaved={isSaved}
                        setIsSaved={setIsSaved}
                      />
                    ))}
                </Grid>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No content for this stage.
              </Typography>
            )}

            {mainValue == 0 || mainValue == 1 ? null : (
              <Box sx={{ m: 3, display: "flex", justifyContent: "flex-end" }}>
                <Button
                  variant="contained"
                  onClick={() => {
                    savefn(stage, mainValue, subValue);
                  }}
                  disabled={isBtnDisabled}
                >
                  {isBtnDisabled ? "Saving..." : "Save"}
                </Button>
              </Box>
            )}
          </CustomTabPanel>
        ))}
      </Paper>

      <Dialog open={showUnsavedDialog} maxWidth="xs" fullWidth>
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: "bold",
            color: "#0977BC",
            mt: 3,
            mb: 2,
            fontFamily: "Geist, sans-serif",
          }}
        >
          Unsaved Changes
        </DialogTitle>
        <DialogContent>
          <Box sx={{ m: 1 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 500, mb: 1, fontFamily: "Geist, sans-serif" }}
            >
              You have unsaved changes. Are you sure you want to switch tabs?
              Your changes will be lost.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ mt: 3, mb: 3, mr: 3 }}>
          <Box display="flex" gap={2}>
            <CommonButton
              variant="outlined"
              customColor="#B0BEC5"
              onClick={() => setShowUnsavedDialog(false)}
            >
              Cancel
            </CommonButton>
            <CommonButton
              variant="contained"
              customColor="#007BFF"
              onClick={() => {
                setShowUnsavedDialog(false);

                if (unsavedDialogLocation == "main") {
                  setMainValue(pendingTabIndex);
                  setSubValue(0);
                } else {
                  setSubValue(pendingSubTabIndex);
                }

                setCurrentProcessFlowData(
                  JSON.parse(JSON.stringify(initialProcessFlowData))
                );
                setFileUploads({});
              }}
            >
              Switch Tabs
            </CommonButton>
          </Box>
        </DialogActions>
      </Dialog>

      <Backdrop
        open={isBtnDisabled}
        sx={{
          zIndex: `9999999999999999`,
          color: "#fff",
          backdropFilter: "blur(5px)",
          backgroundColor: "rgba(0, 0, 0, 0.2)",
          flexDirection: "column",
        }}
      >
        <CircularProgress sx={{ color: "#006CB8", scale: 3 }} size={60} />
      </Backdrop>
    </Paper>
  );
};

export default ProcessFlowViewer;
